import { supabase } from '@/lib/supabase'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { Skeleton } from 'boneyard-js/react'
import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { ColumnVisibilityToggle } from '../../../components/ColumnVisibilityToggle'
import { UnsavedChangesDialog } from '../../../components/UnsavedChangesDialog'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import {
  type ColumnVisibilityDef,
  useColumnVisibility,
} from '../../../hooks/useColumnVisibility'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll'
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges'
import type {
  RoomFormValues as BackendRoomFormValues,
  RoomRow,
} from './interfaces/Room'

const PAGE_SIZE = 20

const HIDDEN_COLS_KEY = 'rooms-hidden-cols'

const COLUMN_DEFS: ColumnVisibilityDef[] = [
  { id: 'room_number', label: 'Room', alwaysVisible: true },
  { id: 'status', label: 'Status' },
  { id: 'room_type', label: 'Type' },
  { id: 'price_monthly', label: 'Monthly (฿)' },
  { id: 'elec_unit_price', label: 'Elec/unit' },
  { id: 'water_unit_price', label: 'Water/unit' },
  { id: 'internet_fee', label: 'Internet' },
  { id: 'furniture_fee', label: 'Furniture' },
  { id: 'maintenance_fee', label: 'Maintenance' },
  { id: 'other_fee', label: 'Other Fee (Default)' },
  { id: 'other_fee_label', label: 'Other Fee Label (Default)' },
  { id: 'keycards', label: 'Keycards' },
  { id: 'remark', label: 'Remark (Default)' },
  { id: 'repair_details', label: 'Repair Details' },
]

type SortColumn =
  | 'room_number'
  | 'status'
  | 'room_type'
  | 'keycards'
  | 'price_monthly'
  | 'elec_unit_price'
  | 'water_unit_price'
  | 'internet_fee'
  | 'furniture_fee'
  | 'maintenance_fee'
  | 'other_fee'
  | 'other_fee_label'
  | 'remark'
  | 'repair_details'

interface SortState {
  column: SortColumn
  ascending: boolean
}

interface FormKeycard {
  id: string
  value: string
}

type RoomFormValues = Omit<BackendRoomFormValues, 'keycards'> & {
  keycards: FormKeycard[]
}

const roomSchema = z.object({
  room_number: z.string().min(1, 'Room number is required'),
  status: z.enum([
    'available',
    'occupied',
    'under_renovation',
    'not_available',
  ]),
  room_type: z.enum(['air', 'fan']),
  price_monthly: z.coerce.number().min(0),
  water_unit_price: z.coerce.number().min(0),
  elec_unit_price: z.coerce.number().min(0),
  internet_fee: z.coerce.number().min(0),
  furniture_fee: z.coerce.number().min(0),
  maintenance_fee: z.coerce.number().min(0),
  other_fee: z.coerce.number(),
  other_fee_label: z.string().optional(),
  remark: z.string().optional(),
  repair_details: z.string().optional(),
  keycards: z.array(z.string()),
})

const createFormKeycard = (value = ''): FormKeycard => ({
  id:
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  value,
})

const emptyForm = (): RoomFormValues => ({
  room_number: '',
  status: 'available',
  room_type: 'fan',
  price_monthly: '0',
  water_unit_price: '0',
  elec_unit_price: '0',
  internet_fee: '0',
  furniture_fee: '0',
  maintenance_fee: '100',
  other_fee: '0',
  other_fee_label: 'ค่าปรับ และค่าบริการอื่นๆเพิ่มเติม',
  remark: '',
  repair_details: '',
  keycards: [],
})

const rowToForm = (row: RoomRow): RoomFormValues => ({
  room_number: row.room_number,
  status: row.status as
    | 'available'
    | 'occupied'
    | 'under_renovation'
    | 'not_available',
  room_type: row.room_type as 'air' | 'fan',
  price_monthly: String(row.price_monthly),
  water_unit_price: String(row.water_unit_price),
  elec_unit_price: String(row.elec_unit_price),
  internet_fee: String(row.internet_fee),
  furniture_fee: String(row.furniture_fee),
  maintenance_fee: String(row.maintenance_fee),
  other_fee: String(row.other_fee),
  other_fee_label: row.other_fee_label,
  remark: row.remark ?? '',
  repair_details: row.repair_details ?? '',
  keycards: (row.keycards ?? []).map((value: string) =>
    createFormKeycard(value),
  ),
})

export default function RoomsPage() {
  const queryClient = useQueryClient()
  console.log('queryClient', queryClient)
  const [searchParams] = useSearchParams()
  const showHeader = searchParams.get('header') !== 'hide'
  const [showForm, setShowForm] = useState(false)
  console.log('showForm', showForm)
  const [editingRoom, setEditingRoom] = useState<RoomRow | null>(null)
  const [isMovingIn, setIsMovingIn] = useState(false)
  console.log('editingRoom', editingRoom)
  const [form, setForm] = useState<RoomFormValues>(emptyForm())
  console.log('form', form)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  console.log('errors', errors)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [roomSearch, setRoomSearch] = useState('')
  const [sortState, setSortState] = useState<SortState>({
    column: 'room_number',
    ascending: true,
  })
  const debouncedRoomSearch = useDebouncedValue(roomSearch, 500)
  const normalizedRoomSearch = debouncedRoomSearch.trim()

  const handleSort = (column: SortColumn) => {
    setSortState((prev) =>
      prev.column === column
        ? { column, ascending: !prev.ascending }
        : { column, ascending: true },
    )
  }

  const sortIndicator = (column: SortColumn) => {
    if (sortState.column !== column) return ' ⇅'
    return sortState.ascending ? ' ↑' : ' ↓'
  }

  const {
    ref: colsDropdownRef,
    open: colsOpen,
    setOpen: setColsOpen,
    hiddenCount,
    isVisible,
    toggleColumn,
  } = useColumnVisibility(HIDDEN_COLS_KEY, COLUMN_DEFS)

  const col = isVisible

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteQuery({
      queryKey: [
        'rooms',
        sortState.column,
        sortState.ascending,
        statusFilter,
        normalizedRoomSearch,
      ],
      initialPageParam: 0,
      queryFn: async ({ pageParam }) => {
        const from = pageParam
        const to = from + PAGE_SIZE - 1
        let query = supabase
          .from('rooms')
          .select('*')
          .is('deleted_at', null)
          .order(sortState.column, { ascending: sortState.ascending })

        // Apply status filter if not 'all'
        if (statusFilter !== 'all') {
          query = query.eq('status', statusFilter)
        }
        if (normalizedRoomSearch) {
          query = query.eq('room_number', normalizedRoomSearch)
        }

        const { data, error } = await query.range(from, to)
        if (error) {
          console.error('Error fetching rooms:', error)
          throw error
        }
        console.log('Fetched rooms data:', data)
        return data as RoomRow[]
      },
      getNextPageParam: (lastPage, _allPages, lastPageParam) =>
        lastPage.length < PAGE_SIZE ? undefined : lastPageParam + PAGE_SIZE,
    })

  const rooms: RoomRow[] = data?.pages.flat() ?? []

  // Query for status counts
  const { data: statusCounts } = useQuery({
    queryKey: ['rooms', 'status-counts', normalizedRoomSearch],
    queryFn: async () => {
      let query = supabase.from('rooms').select('status').is('deleted_at', null)
      if (normalizedRoomSearch) {
        query = query.eq('room_number', normalizedRoomSearch)
      }
      const { data, error } = await query

      if (error) {
        console.error('Error fetching status counts:', error)
        throw error
      }

      const counts = {
        available: 0,
        occupied: 0,
        under_renovation: 0,
        not_available: 0,
      }

      for (const room of data ?? []) {
        if (room.status in counts) {
          counts[room.status as keyof typeof counts]++
        }
      }

      return counts
    },
  })

  const onLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])
  const sentinelRef = useInfiniteScroll(onLoadMore, Boolean(hasNextPage))

  const createMutation = useMutation({
    mutationFn: async (values: BackendRoomFormValues) => {
      const parsed = roomSchema.parse(values)
      const { error } = await supabase.from('rooms').insert(parsed)
      if (error) {
        console.error('Error creating room:', error)
        throw error
      }
    },
    onSuccess: () => {
      console.log('Room created successfully')
      queryClient.invalidateQueries({ queryKey: ['rooms'] })
      setShowForm(false)
      setForm(emptyForm())
      setSubmitError(null)
    },
    onError: (error: Error) => setSubmitError(`Save failed: ${error.message}`),
  })

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      values,
      moveIn,
    }: {
      id: string
      values: BackendRoomFormValues
      moveIn: boolean
    }) => {
      const parsed = roomSchema.parse(values)
      let query = supabase
        .from('rooms')
        .update({ ...parsed, ...(moveIn ? { status: 'occupied' } : {}) })
        .eq('room_number', id)
        .is('deleted_at', null)
      if (moveIn) query = query.eq('status', 'available')
      const { data, error } = await query.select('room_number').maybeSingle()
      if (error) {
        console.error('Error updating room:', error)
        throw error
      }
      if (!data) {
        throw new Error(
          moveIn
            ? 'ห้องนี้ไม่ว่างแล้วหรือไม่สามารถบันทึกได้ กรุณาโหลดรายการห้องใหม่'
            : 'Room could not be updated. Please reload the room list.',
        )
      }
    },
    onSuccess: () => {
      console.log('Room updated successfully')
      queryClient.invalidateQueries({ queryKey: ['rooms'] })
      setShowForm(false)
      setEditingRoom(null)
      setIsMovingIn(false)
      setForm(emptyForm())
      setSubmitError(null)
    },
    onError: (error: Error) => setSubmitError(`Save failed: ${error.message}`),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (createMutation.isPending || updateMutation.isPending) return
    setSubmitError(null)
    const normalizedForm: BackendRoomFormValues = {
      ...form,
      keycards: form.keycards.map((item) => item.value.trim()).filter(Boolean),
    }

    const result = roomSchema.safeParse(normalizedForm)
    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      for (const issue of result.error.issues) {
        const key = issue.path[0] as string
        fieldErrors[key] = issue.message
      }
      console.warn('Validation errors', fieldErrors)
      setErrors(fieldErrors)
      return
    }
    setErrors({})
    if (editingRoom) {
      updateMutation.mutate({
        id: editingRoom.room_number,
        values: normalizedForm,
        moveIn: isMovingIn,
      })
    } else {
      createMutation.mutate(normalizedForm)
    }
  }

  const openCreate = () => {
    setIsMovingIn(false)
    setEditingRoom(null)
    setForm(emptyForm())
    setErrors({})
    setSubmitError(null)
    setShowForm(true)
  }

  const openEdit = (room: RoomRow) => {
    setIsMovingIn(false)
    setEditingRoom(room)
    setForm(rowToForm(room))
    setErrors({})
    setSubmitError(null)
    setShowForm(true)
  }

  const openMoveIn = (room: RoomRow) => {
    setEditingRoom(room)
    setIsMovingIn(true)
    setForm({ ...rowToForm(room), status: 'occupied' })
    setErrors({})
    setSubmitError(null)
    setShowForm(true)
  }

  const isFormDirty = useMemo((): boolean => {
    if (!showForm) return false
    if (editingRoom) {
      const orig = rowToForm(editingRoom)
      const keycardsMatch =
        form.keycards.length === orig.keycards.length &&
        form.keycards.every((k, i) => k.value === orig.keycards[i]?.value)
      return (
        form.room_number !== orig.room_number ||
        form.status !== orig.status ||
        form.room_type !== orig.room_type ||
        form.price_monthly !== orig.price_monthly ||
        form.water_unit_price !== orig.water_unit_price ||
        form.elec_unit_price !== orig.elec_unit_price ||
        form.internet_fee !== orig.internet_fee ||
        form.furniture_fee !== orig.furniture_fee ||
        form.maintenance_fee !== orig.maintenance_fee ||
        form.other_fee !== orig.other_fee ||
        form.other_fee_label !== orig.other_fee_label ||
        form.remark !== orig.remark ||
        form.repair_details !== orig.repair_details ||
        !keycardsMatch
      )
    }
    const empty = emptyForm()
    return (
      form.room_number !== empty.room_number ||
      form.status !== empty.status ||
      form.room_type !== empty.room_type ||
      form.price_monthly !== empty.price_monthly ||
      form.water_unit_price !== empty.water_unit_price ||
      form.elec_unit_price !== empty.elec_unit_price ||
      form.internet_fee !== empty.internet_fee ||
      form.furniture_fee !== empty.furniture_fee ||
      form.maintenance_fee !== empty.maintenance_fee ||
      form.other_fee !== empty.other_fee ||
      form.other_fee_label !== empty.other_fee_label ||
      form.remark !== empty.remark ||
      form.repair_details !== empty.repair_details ||
      form.keycards.length > 0
    )
  }, [showForm, form, editingRoom])

  const { isBlocked, proceed, reset } = useUnsavedChanges(isFormDirty)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState<boolean>(false)

  const closeForm = () => {
    setShowForm(false)
    setEditingRoom(null)
    setIsMovingIn(false)
    setForm(emptyForm())
    setErrors({})
    setSubmitError(null)
  }

  const handleCancelForm = (): void => {
    if (isFormDirty) {
      setShowDiscardConfirm(true)
    } else {
      closeForm()
    }
  }

  const numericFields: {
    key: keyof Omit<RoomFormValues, 'keycards'>
    label: string
  }[] = [
    { key: 'price_monthly', label: 'Monthly Rent (฿)' },
    { key: 'water_unit_price', label: 'Water Unit Price (฿)' },
    { key: 'elec_unit_price', label: 'Elec Unit Price (฿)' },
    { key: 'internet_fee', label: 'Internet Fee (฿)' },
    { key: 'furniture_fee', label: 'Furniture Fee (฿)' },
    { key: 'maintenance_fee', label: 'Maintenance Fee (฿)' },
    { key: 'other_fee', label: 'Other Fee (฿) (Default)' },
  ]
  console.log('numericFields', numericFields)

  function renderHeader() {
    return (
      <>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-left font-semibold text-gray-900 text-lg">
              Rooms
            </h2>
            <p className="text-left text-gray-500 text-xs">
              {rooms.length} room{rooms.length !== 1 ? 's' : ''}
            </p>
            {statusCounts && (
              <div className="mt-2 flex gap-3 text-xs">
                <span className="rounded bg-green-100 px-2 py-1 text-green-700">
                  Available: {statusCounts.available}
                </span>
                <span className="rounded bg-red-100 px-2 py-1 text-red-700">
                  Occupied: {statusCounts.occupied}
                </span>
                <span className="rounded bg-yellow-100 px-2 py-1 text-yellow-700">
                  Under Renovation: {statusCounts.under_renovation}
                </span>
                <span className="rounded bg-gray-200 px-2 py-1 text-gray-600">
                  Not Available: {statusCounts.not_available}
                </span>
              </div>
            )}
          </div>
          <Button onClick={openCreate}>+ New Room</Button>
        </div>
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-2 py-2 text-xs">
          <ColumnVisibilityToggle
            title="Columns"
            columns={COLUMN_DEFS}
            isOpen={colsOpen}
            hiddenCount={hiddenCount}
            wrapperRef={colsDropdownRef}
            isVisible={isVisible}
            onToggle={toggleColumn}
            onToggleOpen={() => setColsOpen((o) => !o)}
          />
          <div className="flex items-center gap-2">
            <Label htmlFor="room-search" className="text-xs">
              Search:
            </Label>
            <Input
              id="room-search"
              type="search"
              placeholder="101"
              value={roomSearch}
              onChange={(e) => setRoomSearch(e.target.value)}
              className="h-7 w-28 text-xs"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="status-filter" className="text-xs">
              Status:
            </Label>
            <select
              id="status-filter"
              title="Filter by status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-md border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All</option>
              <option value="available">Available</option>
              <option value="occupied">Occupied</option>
              <option value="under_renovation">Under Renovation</option>
              <option value="not_available">Not Available</option>
            </select>
          </div>
          <div className="flex-1" />
        </div>
      </>
    )
  }

  console.log('Rendering RoomsPage return')
  return (
    <div className="relative flex h-full flex-col">
      {showHeader && (
        <div className="sticky top-0 z-30 bg-gray-50">{renderHeader()}</div>
      )}

      <Skeleton
        name="admin-rooms"
        loading={isLoading}
        className="flex min-h-0 flex-1 flex-col *:data-boneyard-content:flex *:data-boneyard-content:min-h-0 *:data-boneyard-content:flex-1 *:data-boneyard-content:flex-col"
      >
        <Card className="min-h-0 flex-1 overflow-hidden p-0">
          <div className="h-full overflow-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr className="sticky top-0 z-20 bg-gray-50">
                  {(
                    [
                      { key: 'room_number', label: 'Room', align: 'left' },
                      { key: 'status', label: 'Status', align: 'left' },
                      { key: 'room_type', label: 'Type', align: 'left' },
                      {
                        key: 'price_monthly',
                        label: 'Monthly (฿)',
                        align: 'right',
                      },
                      {
                        key: 'elec_unit_price',
                        label: 'Elec/unit',
                        align: 'right',
                      },
                      {
                        key: 'water_unit_price',
                        label: 'Water/unit',
                        align: 'right',
                      },
                      {
                        key: 'internet_fee',
                        label: 'Internet',
                        align: 'right',
                      },
                      {
                        key: 'furniture_fee',
                        label: 'Furniture',
                        align: 'right',
                      },
                      {
                        key: 'maintenance_fee',
                        label: 'Maintenance',
                        align: 'right',
                      },
                      {
                        key: 'other_fee',
                        label: 'Other Fee (Default)',
                        align: 'right',
                      },
                      {
                        key: 'other_fee_label',
                        label: 'Other Fee Label (Default)',
                        align: 'left',
                      },
                      { key: 'keycards', label: 'Keycards', align: 'left' },
                      {
                        key: 'remark',
                        label: 'Remark (Default)',
                        align: 'left',
                      },
                      {
                        key: 'repair_details',
                        label: 'Repair Details',
                        align: 'left',
                      },
                    ] as {
                      key: SortColumn
                      label: string
                      align: 'left' | 'right'
                    }[]
                  )
                    .filter(({ key }) => col(key))
                    .map(({ key, label, align }) => (
                      <th
                        key={key}
                        className={`px-4 py-3 font-medium text-gray-500 text-${align} ${
                          key === 'room_number'
                            ? 'sticky left-0 z-10 bg-gray-50'
                            : ''
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => handleSort(key)}
                          className={`inline-flex cursor-pointer items-center gap-0.5 hover:text-gray-900 ${
                            sortState.column === key ? 'text-gray-900' : ''
                          }`}
                        >
                          {label}
                          <span className="text-xs">{sortIndicator(key)}</span>
                        </button>
                      </th>
                    ))}
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {rooms.map((room) => (
                  <tr key={room.room_number} className="hover:bg-gray-50">
                    <td className="sticky left-0 z-10 whitespace-nowrap bg-white px-4 py-3 text-left font-medium">
                      {room.room_number}
                    </td>
                    {col('status') && (
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 font-semibold text-xs ${
                            room.status === 'available'
                              ? 'bg-green-100 text-green-700'
                              : room.status === 'occupied'
                                ? 'bg-red-100 text-red-700'
                                : room.status === 'under_renovation'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {room.status}
                        </span>
                      </td>
                    )}
                    {col('room_type') && (
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 font-semibold text-xs ${
                            room.room_type === 'air'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {room.room_type === 'air' ? 'Air' : 'Fan'}
                        </span>
                      </td>
                    )}
                    {col('price_monthly') && (
                      <td className="px-4 py-3 text-right">
                        {Number(room.price_monthly).toLocaleString()}
                      </td>
                    )}
                    {col('elec_unit_price') && (
                      <td className="px-4 py-3 text-right">
                        {Number(room.elec_unit_price).toLocaleString()}
                      </td>
                    )}
                    {col('water_unit_price') && (
                      <td className="px-4 py-3 text-right">
                        {Number(room.water_unit_price).toLocaleString()}
                      </td>
                    )}
                    {col('internet_fee') && (
                      <td className="px-4 py-3 text-right">
                        {Number(room.internet_fee).toLocaleString()}
                      </td>
                    )}
                    {col('furniture_fee') && (
                      <td className="px-4 py-3 text-right">
                        {Number(room.furniture_fee).toLocaleString()}
                      </td>
                    )}
                    {col('maintenance_fee') && (
                      <td className="px-4 py-3 text-right">
                        {Number(room.maintenance_fee).toLocaleString()}
                      </td>
                    )}
                    {col('other_fee') && (
                      <td className="px-4 py-3 text-right">
                        {Number(room.other_fee).toLocaleString()}
                      </td>
                    )}
                    {col('other_fee_label') && (
                      <td className="px-4 py-3 text-sm">
                        {room.other_fee_label ?? '—'}
                      </td>
                    )}
                    {col('keycards') && (
                      <td className="px-4 py-3 text-sm">
                        {room.keycards.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {room.keycards.map((keycard) => (
                              <span
                                key={keycard}
                                className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700 text-xs"
                              >
                                {keycard}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-500">—</span>
                        )}
                      </td>
                    )}
                    {col('remark') && (
                      <td className="px-4 py-3 text-gray-600 text-sm">
                        {room.remark ?? '—'}
                      </td>
                    )}
                    {col('repair_details') && (
                      <td className="px-4 py-3 text-gray-600 text-sm">
                        {room.repair_details ?? '—'}
                      </td>
                    )}
                    <td className="px-4 py-3 text-right">
                      {room.status === 'available' && (
                        <Button
                          size="sm"
                          onClick={() => openMoveIn(room)}
                          className="mr-2"
                        >
                          รับย้ายเข้า
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEdit(room)}
                        className="mr-2"
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div ref={sentinelRef} className="h-6" />
          {isFetchingNextPage && (
            <div className="py-2 text-center text-gray-500">Loading more…</div>
          )}
        </Card>
      </Skeleton>

      {/* Create / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <dialog
            open
            aria-modal="true"
            aria-labelledby="room-form-title"
            className="relative m-0 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border bg-white p-6 shadow-sm"
          >
            <h3 id="room-form-title" className="mb-4 font-semibold text-lg">
              {isMovingIn
                ? `รับย้ายเข้า ห้อง ${editingRoom?.room_number}`
                : editingRoom
                  ? 'Edit Room'
                  : 'New Room'}
            </h3>
            {isMovingIn && (
              <p className="mb-4 text-gray-600 text-sm">
                ตรวจค่าเช่า ค่าบริการ และคีย์การ์ดก่อนบันทึก ห้องจะเปลี่ยนเป็น Occupied
                โดยไม่ต้องสร้างผู้เช่าหรือ Contract
              </p>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="room_number">Room Number</Label>
                <Input
                  id="room_number"
                  value={form.room_number}
                  disabled={!!editingRoom}
                  onChange={(e) =>
                    setForm({ ...form, room_number: e.target.value })
                  }
                />
                {errors.room_number && (
                  <p className="mt-1 text-red-500 text-xs">
                    {errors.room_number}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  title="Status"
                  value={form.status}
                  disabled={isMovingIn}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      status: e.target.value as
                        | 'available'
                        | 'occupied'
                        | 'under_renovation'
                        | 'not_available',
                    })
                  }
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="available">Available</option>
                  <option value="occupied">Occupied</option>
                  <option value="under_renovation">Under Renovation</option>
                  <option value="not_available">Not Available</option>
                </select>
              </div>

              <div>
                <Label htmlFor="room_type">Room Type</Label>
                <select
                  id="room_type"
                  title="Room Type"
                  value={form.room_type}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      room_type: e.target.value as 'air' | 'fan',
                    })
                  }
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="fan">Fan</option>
                  <option value="air">Air</option>
                </select>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <Label htmlFor="keycards">Keycards</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        keycards: [...prev.keycards, createFormKeycard('')],
                      }))
                    }
                  >
                    Add Card
                  </Button>
                </div>

                <div className="space-y-2">
                  {form.keycards.length === 0 && (
                    <p className="text-gray-500 text-sm">No keycards added.</p>
                  )}

                  {form.keycards.map((keycard) => (
                    <div key={keycard.id} className="flex items-center gap-2">
                      <Input
                        id={`keycard-${keycard.id}`}
                        placeholder="Card number"
                        value={keycard.value}
                        onChange={(e) => {
                          setForm((prev) => ({
                            ...prev,
                            keycards: prev.keycards.map((item) =>
                              item.id === keycard.id
                                ? { ...item, value: e.target.value }
                                : item,
                            ),
                          }))
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            keycards: prev.keycards.filter(
                              (item) => item.id !== keycard.id,
                            ),
                          }))
                        }
                        className="text-red-600 hover:bg-red-50"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {numericFields.map(({ key, label }) => (
                  <div key={key}>
                    <Label htmlFor={key}>{label}</Label>
                    <Input
                      id={key}
                      type="number"
                      min={key === 'other_fee' ? undefined : '0'}
                      step="0.01"
                      value={form[key]}
                      readOnly={key === 'other_fee'}
                      aria-describedby={
                        key === 'other_fee' ? 'room-defaults-help' : undefined
                      }
                      className="read-only:bg-gray-50 read-only:text-gray-500"
                      onChange={
                        key === 'other_fee'
                          ? undefined
                          : (e) => setForm({ ...form, [key]: e.target.value })
                      }
                    />
                    {errors[key] && (
                      <p className="mt-1 text-red-500 text-xs">{errors[key]}</p>
                    )}
                  </div>
                ))}
              </div>

              <div>
                <Label htmlFor="other_fee_label">
                  Other Fee Label (Default)
                </Label>
                <Input
                  id="other_fee_label"
                  type="text"
                  value={form.other_fee_label}
                  readOnly
                  aria-describedby="room-defaults-help"
                  className="bg-gray-50 text-gray-500"
                />
                {errors.other_fee_label && (
                  <p className="mt-1 text-red-500 text-xs">
                    {errors.other_fee_label}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="remark">Remark (Default)</Label>
                <textarea
                  id="remark"
                  placeholder="No default remark"
                  value={form.remark ?? ''}
                  readOnly
                  aria-describedby="room-defaults-help"
                  rows={3}
                  className="mt-1 w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <p id="room-defaults-help" className="text-gray-500 text-xs">
                Other Fee, Other Fee Label, and Remark are read-only room
                defaults. Adjust monthly values in Monthly Records.
              </p>

              <div>
                <Label htmlFor="repair_details">Repair Details</Label>
                <textarea
                  id="repair_details"
                  placeholder="Details about what needs to be repaired (for under renovation status)"
                  value={form.repair_details ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, repair_details: e.target.value })
                  }
                  rows={3}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelForm}
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {isMovingIn
                    ? 'บันทึกรับย้ายเข้า'
                    : editingRoom
                      ? 'Save Changes'
                      : 'Create Room'}
                </Button>
              </div>
              {submitError && (
                <p role="alert" className="text-red-600 text-sm">
                  {submitError}
                </p>
              )}
            </form>
          </dialog>
        </div>
      )}

      <UnsavedChangesDialog
        isOpen={isBlocked}
        title="Unsaved Changes"
        description="You have unsaved changes in the room form. Are you sure you want to leave without saving? Your changes will be lost."
        confirmText="Leave without saving"
        cancelText="Stay on page"
        onConfirm={proceed}
        onCancel={reset}
      />

      <UnsavedChangesDialog
        isOpen={showDiscardConfirm}
        title="Discard Room Changes?"
        description="You have unsaved changes in this form. Are you sure you want to discard them?"
        confirmText="Discard changes"
        cancelText="Keep editing"
        onConfirm={() => {
          setShowDiscardConfirm(false)
          closeForm()
        }}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    </div>
  )
}
